import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wind, Waves, Play, Pause, Info, Volume2 } from 'lucide-react';
import { startFrequencySession, stopFrequencySession, VESSELS } from '../../utils/frequencySounds';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   THE FREQUENCY — Air breath + Vessel-tone therapy
   ═══════════════════════════════════════════════════════════════════ */

const NAVY    = '#050507';
const NAVY_HI = '#0E0E14';
const GOLD    = '#D4AF37';
const GOLD_HI = '#FFE9A8';
const GOLD_LO = '#7A5418';

const DURATIONS = [3, 5, 10, 15, 20]; // minutes

const haptic = (ms = 12) => {
  try { nativeVibrate(ms); } catch (_) { /* no-op */ }
};

/* ── Tile icon ──────────────────────────────────────────────────── */
export const FrequencyTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40"
    style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.65))' }}>
    <defs>
      <linearGradient id="freqGold" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%"  stopColor="#FFF1B8"/>
        <stop offset="50%" stopColor="#D4AF37"/>
        <stop offset="100%" stopColor="#5A3F10"/>
      </linearGradient>
    </defs>
    {/* Waveform */}
    <path d="M 4 20 Q 8 12, 12 20 T 20 20 T 28 20 T 36 20"
      fill="none" stroke="url(#freqGold)" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 4 26 Q 8 22, 12 26 T 20 26 T 28 26 T 36 26"
      fill="none" stroke="url(#freqGold)" strokeWidth="1.5" opacity="0.55" strokeLinecap="round"/>
    <path d="M 4 14 Q 8 10, 12 14 T 20 14 T 28 14 T 36 14"
      fill="none" stroke="url(#freqGold)" strokeWidth="1.5" opacity="0.55" strokeLinecap="round"/>
    {/* Central pulse dot */}
    <circle cx="20" cy="20" r="2.2" fill="#FFE9A8"/>
  </svg>
);

/* ── Format seconds → M:SS ────────────────────────────────────────── */
const fmt = (secTotal) => {
  const s = Math.max(0, Math.floor(secTotal || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
};

/* ═══════════════════════════════════════════════════════════════════
   Vessel row (compact tap-to-play, one-page fit for 5 vessels)
   ═══════════════════════════════════════════════════════════════════ */
const VesselRow = ({ vessel, onPlay }) => (
  <button type="button" onClick={() => onPlay(vessel.id)}
    className="w-full text-left rounded-xl p-2.5 flex items-center gap-2.5 transition-transform active:scale-[0.98]"
    style={{
      background: `linear-gradient(160deg, ${NAVY_HI}, ${NAVY})`,
      border: `1.5px solid ${vessel.color}55`,
      boxShadow: `0 0 12px ${vessel.color}22, inset 0 0 8px ${vessel.color}0F`,
    }}
    data-testid={`freq-vessel-${vessel.id}`}>
    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        background: `radial-gradient(circle, ${vessel.color}55, transparent 70%)`,
        border: `1px solid ${vessel.color}`,
        boxShadow: `0 0 10px ${vessel.color}66`,
      }}>
      <Play size={13} color={vessel.color}/>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-1.5">
        <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.24em]" style={{ color: vessel.color }}>
          {vessel.intent}
        </span>
        <span className="font-['Cormorant_Garamond'] text-base leading-none" style={{ color: GOLD_HI }}>
          · {vessel.hz} Hz
        </span>
      </div>
      <p className="mt-0.5 font-['Cormorant_Garamond'] italic text-[11px] leading-snug line-clamp-2"
        style={{ color: `${GOLD_HI}BB` }}>
        {vessel.principle}
      </p>
    </div>
  </button>
);

/* ═══════════════════════════════════════════════════════════════════
   Session runner (playback + progress + halo animation)
   ═══════════════════════════════════════════════════════════════════ */
const SessionRunner = ({ mode, vessel, totalMinutes, onExit }) => {
  const [tick, setTick] = useState({ phase: 'tone', secondsIntoPhase: 0, elapsedSec: 0, remainingSec: totalMinutes * 60 });
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const sessionRef = useRef(null);
  const accent = mode === 'air' ? GOLD_HI : (vessel?.color || GOLD_HI);

  const start = useCallback(() => {
    setDone(false);
    sessionRef.current = startFrequencySession({
      mode,
      hz: vessel?.hz || 432,
      totalMinutes,
      onTick: (state) => setTick(state),
      onComplete: () => { setRunning(false); setDone(true); },
    });
    setRunning(true);
    haptic(30);
  }, [mode, vessel, totalMinutes]);

  const stop = useCallback(() => {
    stopFrequencySession();
    sessionRef.current = null;
    setRunning(false);
    haptic(15);
  }, []);

  useEffect(() => () => { stopFrequencySession(); }, []);

  // Auto-start on mount (safe because we entered via a user-gesture click on Begin).
  useEffect(() => {
    start();
  }, [start]);

  const isTone = tick.phase === 'tone';
  // Halo scale: swells with tone phase
  const t = Math.min(1, tick.secondsIntoPhase / 12);
  const haloScale = isTone
    ? 0.85 + Math.sin(t * Math.PI) * 0.35   // low→high→low over tone phase
    : 0.75;                                  // void = small steady

  return (
    <div className="flex-1 flex flex-col items-center justify-between px-6 pt-4 pb-8"
      data-testid="freq-session-runner">
      <div className="text-center">
        <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: accent }}>
          {mode === 'air' ? 'Air · Wind' : `${vessel.intent} · ${vessel.hz} Hz`}
        </p>
        <p className="mt-1 font-['Cormorant_Garamond'] italic text-sm" style={{ color: `${GOLD}AA` }}>
          {isTone ? 'Listen · settle into the swell' : 'Void · visualise your dream'}
        </p>
      </div>

      {/* Central pulsing halo */}
      <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}
        data-testid="freq-halo">
        <motion.div
          animate={{ scale: haloScale, opacity: isTone ? 0.75 : 0.35 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="absolute rounded-full"
          style={{
            width: 240, height: 240,
            background: `radial-gradient(circle, ${accent}44 0%, ${accent}12 50%, transparent 75%)`,
            boxShadow: `0 0 60px ${accent}77, inset 0 0 40px ${accent}44`,
          }}/>
        <motion.div
          animate={{ scale: haloScale * 0.55, opacity: isTone ? 1 : 0.5 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="absolute rounded-full"
          style={{
            width: 240, height: 240,
            background: `radial-gradient(circle, ${accent}CC 0%, ${accent}44 40%, transparent 70%)`,
          }}/>
        <div className="relative text-center z-10">
          <p className="font-['Cormorant_Garamond'] text-3xl leading-none" style={{ color: '#050507' }}>
            {fmt(tick.remainingSec)}
          </p>
          <p className="mt-1 font-['Outfit'] text-[9px] uppercase tracking-[0.28em]" style={{ color: '#050507' }}>
            {isTone ? 'Tone' : 'Void'}
          </p>
        </div>
      </div>

      <div className="w-full space-y-3">
        {done ? (
          <div className="text-center">
            <p className="font-['Cormorant_Garamond'] text-lg" style={{ color: accent }}>Session Sealed ✦</p>
            <button type="button" onClick={onExit}
              className="mt-3 w-full py-2.5 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.24em]"
              style={{
                background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
                color: '#050507', boxShadow: `0 0 12px ${GOLD}AA`,
              }}
              data-testid="freq-session-close">
              Return
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {running ? (
              <button type="button" onClick={stop}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.24em]"
                style={{
                  background: 'transparent',
                  border: `1px solid ${accent}`,
                  color: accent,
                }}
                data-testid="freq-session-stop">
                <Pause size={13}/> Pause & Exit
              </button>
            ) : (
              <button type="button" onClick={start}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.24em]"
                style={{
                  background: `linear-gradient(135deg, ${accent}, ${accent}77)`,
                  color: '#050507', boxShadow: `0 0 12px ${accent}AA`,
                }}
                data-testid="freq-session-start">
                <Play size={13}/> Begin Session
              </button>
            )}
            <button type="button" onClick={onExit}
              className="px-4 py-2.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em]"
              style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}CC` }}
              data-testid="freq-session-back">
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Setup view — mode + duration + vessel list (one-page, no scroll in vessel)
   ═══════════════════════════════════════════════════════════════════ */
const SetupView = ({
  mode, setMode,
  minutes, setMinutes,
  customMin, setCustomMin,
  onBegin,        // used for Air only
  onPlayVessel,   // vessel-row tap → starts session with that vessel
}) => (
  <div className={`flex-1 flex flex-col ${mode === 'vessel' ? 'overflow-hidden' : 'overflow-y-auto'}`}
    data-testid="freq-setup-view">
    <div className="px-5 pt-3 flex-shrink-0">
      <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>
        The Frequency
      </p>
      <h2 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>
        {mode === 'vessel'
          ? 'Tap a vessel to begin.'
          : 'Close your eyes. Listen.'}
      </h2>
    </div>

    {/* Mode toggle */}
    <div className="px-5 mt-2.5 flex-shrink-0">
      <div className="grid grid-cols-2 gap-1.5 rounded-full p-1"
        style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33` }}>
        {[
          { id: 'air',    label: 'Air',    Icon: Wind },
          { id: 'vessel', label: 'Vessel', Icon: Waves },
        ].map((m) => {
          const active = mode === m.id;
          return (
            <button key={m.id} type="button" onClick={() => setMode(m.id)}
              className="flex items-center justify-center gap-1.5 py-1.5 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.22em] transition-all"
              style={{
                background: active ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})` : 'transparent',
                color: active ? '#050507' : GOLD_HI,
                boxShadow: active ? `0 0 12px ${GOLD}88` : 'none',
              }}
              data-testid={`freq-mode-${m.id}`}>
              <m.Icon size={12}/> {m.label}
            </button>
          );
        })}
      </div>
    </div>

    {/* Duration */}
    <div className="px-5 mt-2.5 flex-shrink-0">
      <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.24em]" style={{ color: `${GOLD}CC` }}>
        Duration
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {DURATIONS.map((d) => {
          const active = minutes === d;
          return (
            <button key={d} type="button" onClick={() => { setMinutes(d); setCustomMin(''); }}
              className="px-2.5 py-1 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.18em] transition-all"
              style={{
                background: active ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})` : 'transparent',
                border: `1px solid ${active ? GOLD_HI : `${GOLD}44`}`,
                color: active ? '#050507' : GOLD_HI,
                boxShadow: active ? `0 0 8px ${GOLD}77` : 'none',
              }}
              data-testid={`freq-duration-${d}`}>
              {d} min
            </button>
          );
        })}
        <div className="flex items-center gap-1 rounded-full px-2 py-0.5"
          style={{ border: `1px solid ${customMin ? GOLD_HI : `${GOLD}44`}`, background: 'rgba(255,255,255,0.02)' }}>
          <input
            type="number" min={1} max={60}
            value={customMin}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, '');
              setCustomMin(v);
              const n = parseInt(v, 10);
              if (n >= 1 && n <= 60) setMinutes(n);
            }}
            placeholder="Custom"
            className="bg-transparent outline-none font-['Outfit'] text-[11px] w-14"
            style={{ color: GOLD_HI }}
            data-testid="freq-duration-custom"/>
          <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.15em]" style={{ color: `${GOLD}AA` }}>
            min
          </span>
        </div>
      </div>
    </div>

    {/* Content */}
    {mode === 'vessel' ? (
      <div className="px-5 mt-2.5 pb-3 flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center gap-1.5 mb-1.5 flex-shrink-0">
          <Volume2 size={11} color={GOLD}/>
          <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>
            Imperial Frequency Lexicon
          </p>
        </div>
        <div className="flex-1 flex flex-col gap-1.5 min-h-0">
          {VESSELS.map((v) => (
            <div key={v.id} className="flex-1 min-h-0">
              <VesselRow vessel={v} onPlay={onPlayVessel}/>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="px-5 mt-3 pb-6 flex-shrink-0">
        <div className="rounded-xl p-3"
          style={{ border: `1px solid ${GOLD}44`, background: NAVY_HI }}
          data-testid="freq-air-description">
          <div className="flex items-center gap-1.5">
            <Info size={12} color={GOLD}/>
            <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>
              Air · Wind
            </p>
          </div>
          <p className="mt-1.5 font-['Cormorant_Garamond'] text-[13px] leading-snug" style={{ color: GOLD_HI }}>
            Warm filtered pink-noise rises and falls like wind through a cave. Close your eyes, let your
            body settle into its own rhythm, and simply listen. Visualise your dream in the void between
            each swell.
          </p>
        </div>
        <button type="button" onClick={onBegin}
          className="mt-4 w-full py-3 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.28em] flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
            color: '#050507',
            boxShadow: `0 0 16px ${GOLD}AA`,
          }}
          data-testid="freq-begin">
          <Play size={13}/> Begin {minutes}-min Air Session
        </button>
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   Root — TheFrequency modal
   ═══════════════════════════════════════════════════════════════════ */
export const TheFrequency = ({ onClose }) => {
  const [mode, setMode] = useState('vessel'); // 'air' | 'vessel'
  const [vesselId, setVesselId] = useState('peace');
  const [minutes, setMinutes] = useState(10);
  const [customMin, setCustomMin] = useState('');
  const [phase, setPhase] = useState('setup'); // 'setup' | 'session'

  useEffect(() => () => stopFrequencySession(), []);

  // Back walks: session → setup → close.
  useHistoryClose(true, useCallback(() => {
    if (phase !== 'setup') {
      stopFrequencySession();
      setPhase('setup');
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) {}
    } else {
      stopFrequencySession();
      onClose();
    }
  }, [phase, onClose]));

  const beginSession = () => {
    if (!minutes || minutes < 1) { setMinutes(5); }
    setPhase('session');
    haptic(20);
  };
  const playVessel = (id) => {
    setVesselId(id);
    if (!minutes || minutes < 1) { setMinutes(5); }
    setMode('vessel');
    setPhase('session');
    haptic(20);
  };
  const exitSession = () => {
    stopFrequencySession();
    setPhase('setup');
  };

  const vessel = VESSELS.find((v) => v.id === vesselId);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] overflow-hidden flex flex-col imperium-shell"
      style={{ background: '#020203' }}
      data-testid="freq-modal">

      {/* Header */}
      <div className="relative z-20 flex-shrink-0 backdrop-blur-md"
        style={{ background: `linear-gradient(180deg, ${NAVY}E6, ${NAVY}99)`, borderBottom: `1px solid ${GOLD}33` }}>
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <BackPill onClick={() => { stopFrequencySession(); onClose(); }} accent="#7DD3C0" testId="freq-close"/>
            <FrequencyTileIcon size={22}/>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em] truncate" style={{ color: GOLD }}>
              The Frequency
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <AnimatePresence mode="wait">
        {phase === 'setup' && (
          <motion.div key="setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex-1 overflow-hidden flex flex-col">
            <SetupView
              mode={mode} setMode={setMode}
              minutes={minutes} setMinutes={setMinutes}
              customMin={customMin} setCustomMin={setCustomMin}
              onBegin={beginSession}
              onPlayVessel={playVessel}/>
          </motion.div>
        )}
        {phase === 'session' && (
          <motion.div key="session" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex-1 overflow-hidden flex flex-col">
            <SessionRunner
              mode={mode}
              vessel={vessel}
              totalMinutes={minutes}
              onExit={exitSession}/>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TheFrequency;
